// File Metadata:
// Filename: errorHandler.js
// Version: 1.00
// Created: 2025-05-20
// Last Modified: 2025-05-20
// Authors: Dieuwer Descheemaecker, GitHub Copilot
// Description: Centralized error handling utilities for JWT and authentication errors

import { store } from '../store';
import { logout, refreshToken } from '../store/slices/userSlice';
import { logAuthEvent } from './authLogger';

/**
 * Handle authentication errors from API responses
 * @param {Object} error - Axios error object
 * @returns {Promise<boolean>} - Returns true if error was handled and request should be retried
 */
export const handleAuthError = async (error) => {
  const errorData = error.response?.data;
  const errorCode = errorData?.code;
  
  console.log('=== handleAuthError called ===');
  console.log('Error status:', error.response?.status);
  console.log('Error data:', errorData);
  console.log('Error code:', errorCode);
  console.log('Request URL:', error.config?.url);
  
  // Only handle 401 errors
  if (error.response?.status !== 401) {
    console.log('Not a 401 error, skipping auth error handling');
    return false;
  }
  
  console.log('Handling auth error:', errorCode, errorData?.error);
  
  // Log the authentication event
  logAuthEvent('auth_error_received', {
    errorCode,
    errorMessage: errorData?.error,
    status: error.response?.status,
    url: error.config?.url
  });
    // For any 401 error, attempt to refresh the token
  // This handles both TOKEN_EXPIRED and cases where the backend doesn't provide a code
  try {
    console.log('Authorization error, attempting token refresh...');
    logAuthEvent('token_refresh_attempt', { reason: errorCode || 'unauthorized' });
    
    const refreshResult = await store.dispatch(refreshToken());
    
    console.log('Refresh result:', refreshResult);
    
    if (!refreshResult.error) {
      console.log('Token refreshed successfully');
      logAuthEvent('token_refresh_success', {});
      return true; // Indicate that the request should be retried
    } else {
      console.log('Token refresh failed:', refreshResult.error);
      logAuthEvent('token_refresh_failed', { error: refreshResult.error });
      
      // Only log out if this wasn't a missing refresh token error
      // This prevents logout loops when there's no refresh token
      if (refreshResult.error?.status !== 401 || 
          !refreshResult.error?.message?.includes('No refresh token')) {
        handleLogout('Token refresh failed');
      }
      return false;
    }
  } catch (refreshError) {
    console.error('Token refresh exception:', refreshError);
    logAuthEvent('token_refresh_exception', { error: refreshError.message });
    handleLogout('Token refresh exception');
    return false;
  }
};

/**
 * Handle logout and redirect to login page
 * @param {string} reason - Reason for logout (for logging)
 */
export const handleLogout = (reason = 'Authentication failed') => {
  console.log('Logging out user:', reason);
  
  // Log the logout event
  logAuthEvent('user_logged_out', { reason });
  
  // Dispatch logout action
  store.dispatch(logout());
  
  // Redirect to login page if not already there
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

/**
 * Handle Socket.IO authentication errors
 * @param {string} errorMessage - Error message from Socket.IO
 * @returns {Promise<boolean>} - Returns true if reconnection should be attempted
 */
export const handleSocketAuthError = async (errorMessage) => {
  console.log('Handling socket auth error:', errorMessage);
  
  // Log the socket authentication error
  logAuthEvent('socket_auth_error', { errorMessage });
  
  if (errorMessage.includes('Token expired')) {
    try {
      console.log('Socket token expired, attempting refresh...');
      logAuthEvent('socket_token_refresh_attempt', { reason: 'socket_token_expired' });
      
      const refreshResult = await store.dispatch(refreshToken());
      
      if (!refreshResult.error) {
        console.log('Token refreshed for socket, reconnection should be attempted');
        logAuthEvent('socket_token_refresh_success', {});
        return true; // Indicate that reconnection should be attempted
      } else {
        console.log('Socket token refresh failed:', refreshResult.error);
        logAuthEvent('socket_token_refresh_failed', { error: refreshResult.error });
        handleLogout('Socket token refresh failed');
        return false;
      }
    } catch (refreshError) {
      console.error('Socket token refresh exception:', refreshError);
      logAuthEvent('socket_token_refresh_exception', { error: refreshError.message });
      handleLogout('Socket token refresh exception');
      return false;
    }
  } else {
    // For other socket auth errors, log out immediately
    logAuthEvent('socket_auth_failed', { errorMessage });
    handleLogout(`Socket authentication failed: ${errorMessage}`);
    return false;
  }
};

/**
 * Check if an error is authentication-related
 * @param {Object} error - Error object
 * @returns {boolean} - True if the error is auth-related
 */
export const isAuthError = (error) => {
  return error.response?.status === 401 || 
         error.message?.includes('Authentication') ||
         error.message?.includes('token') ||
         error.message?.includes('Unauthorized');
};

/**
 * Get user-friendly error message from API error
 * @param {Object} error - Error object
 * @returns {string} - User-friendly error message
 */
export const getUserFriendlyErrorMessage = (error) => {
  const errorData = error.response?.data;
  const errorCode = errorData?.code;
  
  switch (errorCode) {
    case 'TOKEN_EXPIRED':
      return 'Your session has expired. Please wait while we refresh your login...';
    case 'TOKEN_INVALID':
      return 'Your session is invalid. Please log in again.';
    case 'USER_NOT_FOUND':
      return 'User account not found. Please log in again.';
    case 'AUTH_FAILED':
      return 'Authentication failed. Please log in again.';
    default:
      return errorData?.error || error.message || 'An unexpected error occurred.';
  }
};

export default {
  handleAuthError,
  handleLogout,
  handleSocketAuthError,
  isAuthError,
  getUserFriendlyErrorMessage
};
